import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import './IntegrationHubPage.css';

const IntegrationHubPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [config, setConfig] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get('/api/connector/config');
        setConfig(res.data?.config || null);
      } catch (err) {
        setError('Failed to load integration configuration.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const baseUrl = useMemo(() => window.location.origin, []);

  const sites = useMemo(() => {
    const raw = config?.embedSettings?.externalSites;
    if (!Array.isArray(raw)) return [];
    return raw.filter((site) => site?.enabled);
  }, [config]);

  const integrationMode = config?.embedSettings?.integrationMode === 'live' ? 'live' : 'testing';

  if (loading) {
    return <div className="integration-hub-page"><p>Loading integration hub...</p></div>;
  }

  return (
    <div className="integration-hub-page">
      <div className="integration-hub-header">
        <div>
          <h1>Integration Hub</h1>
          <p>
            Manage embed handoff values for partner and WordPress sites.
            {' '}Current mode: <strong>{integrationMode}</strong>.
          </p>
        </div>
        <div className="integration-hub-actions">
          <Link to="/admin/connector-settings" className="integration-btn">Open Integrations Settings</Link>
          <Link to="/admin-dashboard" className="integration-btn integration-btn-secondary">Back to Admin</Link>
        </div>
      </div>

      {error && <p className="integration-error">{error}</p>}

      {!config?.embedSettings?.enabled && (
        <div className="integration-card">
          <h2>Embed Integration Disabled</h2>
          <p>Enable public embed integration in Integrations Settings before using this hub.</p>
        </div>
      )}

      {config?.embedSettings?.enabled && sites.length === 0 && (
        <div className="integration-card">
          <h2>No External Sites Enabled</h2>
          <p>Create at least one enabled external site mapping in Integrations Settings.</p>
        </div>
      )}

      {sites.map((site, index) => {
        const siteKey = site.siteKey || `site-${index + 1}`;
        const tenantSlug = site.tenantSlug || 'public';
        const mapUrl = `${baseUrl}/harvesting/map?embedded=1&tenant=${encodeURIComponent(tenantSlug)}`;
        const logUrl = `${baseUrl}/data/harvesting?embedded=1&tenant=${encodeURIComponent(tenantSlug)}`;
        const manifestUrl = `${baseUrl}/api/connector/public/embed-manifest?siteKey=${encodeURIComponent(siteKey)}`;
        const loginUrl = `${baseUrl}/api/connector/public/embed-login`;
        const cipBaseUrl = (config?.embedSettings?.cipBaseUrl || '').replace(/\/$/, '');
        const cipConsultationUrl = cipBaseUrl
          ? `${cipBaseUrl}/t/${encodeURIComponent(tenantSlug)}/consultation?embedded=1&linkMode=${integrationMode}`
          : '';
        const cipCommunityDataUrl = cipBaseUrl
          ? `${cipBaseUrl}/t/${encodeURIComponent(tenantSlug)}/community-data?embedded=1&linkMode=${integrationMode}`
          : '';

        const iframeSnippet = [
          `<iframe src="${mapUrl}" width="100%" height="640" style="border:0; border-radius:12px;" loading="lazy" allowfullscreen></iframe>`,
          '',
          `<iframe src="${logUrl}" width="100%" height="720" style="border:0; border-radius:12px;" loading="lazy"></iframe>`,
        ].join('\n');

        const signedPayloadExample = [
          'siteKey|email|timestamp|nonce|next',
          `Example: ${siteKey}|user@example.com|1731792000000|a7f3f2f4-...|/harvesting`,
          'signature = HMAC_SHA256(payload, accessKey)',
        ].join('\n');

        return (
          <div className="integration-card" key={`${siteKey}-${index}`}>
            <div className="integration-site-head">
              <h2>{site.siteName || siteKey}</h2>
              <span className="integration-badge">tenant: {tenantSlug}</span>
            </div>

            <div className="integration-grid">
              <div>
                <label>Manifest URL</label>
                <textarea className="integration-text" readOnly rows={2} value={manifestUrl} />
              </div>
              <div>
                <label>Login URL</label>
                <textarea className="integration-text" readOnly rows={2} value={loginUrl} />
              </div>
            </div>

            <div className="integration-grid">
              <div>
                <label>Embeddable URLs</label>
                <textarea className="integration-text" readOnly rows={6} value={[
                  `Harvest Map: ${mapUrl}`,
                  `Harvest Log: ${logUrl}`,
                  `CIP Consultation: ${cipConsultationUrl || '(configure CIP base URL in Integrations)'}`,
                  `CIP Community Data: ${cipCommunityDataUrl || '(configure CIP base URL in Integrations)'}`,
                ].join('\n')} />
              </div>
              <div>
                <label>Iframe Snippets</label>
                <textarea className="integration-text" readOnly rows={6} value={iframeSnippet} />
              </div>
            </div>

            <div className="integration-grid">
              <div>
                <label>Signed Payload Format</label>
                <textarea className="integration-text" readOnly rows={4} value={signedPayloadExample} />
              </div>
              <div>
                <label>Request Requirements</label>
                <textarea
                  className="integration-text"
                  readOnly
                  rows={4}
                  value={[
                    `Auto-create users: ${site.allowAutoCreate ? 'enabled' : 'disabled'}`,
                    `Signed payload required: ${site.requireSignedPayload !== false ? 'yes' : 'no'}`,
                    `Origin lock: ${site.origin || 'not set'}`,
                  ].join('\n')}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default IntegrationHubPage;
